import {
  AdminInput,
  AIStarsIcon,
  Button,
  Callout,
  Disclaimer,
  Div,
  H5,
  Icon,
  lightTheme,
  LoadingDots,
  ModalPortal,
  P,
  useIsMobile,
  VDriveStrategySection,
  VDriveStrategySectionKey,
  VDriveStrategySuggestion,
} from '@vgrubs/components';
import { useState } from 'react';
import { SuggestionOverview } from './components/SuggestionOverview';
import { SuggestionSection } from './components/SuggestionSection';
import { dataLine, isApplicable } from './helpers/strategySuggestionFormat';
import { StrategySuggestionStatus } from './hooks/useStrategySuggestion';
import { BulletList } from './StrategySuggestion.styled';

/** The backend refuses a longer note with a 400. */
const NOTE_MAX_LENGTH = 1000;

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  /** "location" or "brand", for the copy. */
  scopeLabel: string;
  status: StrategySuggestionStatus;
  result: VDriveStrategySuggestion | null;
  errorMessage: string | null;
  onStart: (note: string) => void;
  /** Forgets the job, back to the note. */
  onReset: () => void;
  /** Fills the form with these sections. Nothing is saved here. */
  onApply: (sections: VDriveStrategySection[]) => void;
  /**
   * Opened away from the strategy screen (the dashboard): Apply opens that
   * screen with the changes filled in, and the copy says so.
   */
  appliesOnStrategyScreen?: boolean;
  /** Names the location in the intro when the screen around it does not. */
  name?: string;
  /** Above the modal it was opened from, when it was opened from one. */
  zIndex?: number;
};

const Working = () => (
  <Div $m="8px 0">
    {/* The dots are a span, which text paints red unless told. */}
    <P
      $m="0"
      $center
      $color={lightTheme.lightGray}
      $spanColor={lightTheme.lightGray}
    >
      Analyzing the last 90 days
      <LoadingDots active />
    </P>
    <Disclaimer $m="4px 0 0" $center $color={lightTheme.lightGray}>
      This takes 1-3 minutes. You can close this window, the analysis keeps
      running while you stay on this page.
    </Disclaimer>
  </Div>
);

/**
 * "Improve with AI" for a delivery assignment strategy: an optional note,
 * then the minutes-long run, then the AI's answer per section of the screen
 * to tick and apply to the form.
 *
 * `glow` is passed on every render on purpose: the modal mounts its glow
 * wrapper whenever the prop is present, so the border fades out when the
 * answer lands instead of the whole modal remounting (see modal.tsx).
 */
export const StrategySuggestionModal = ({
  isOpen,
  closeModal,
  scopeLabel,
  status,
  result,
  errorMessage,
  onStart,
  onReset,
  onApply,
  appliesOnStrategyScreen = false,
  name,
  zIndex,
}: Props) => {
  const [note, setNote] = useState('');
  // Sections the admin unticked, kept per result: every applicable section
  // starts ticked, and a new result starts over.
  const [unticked, setUnticked] = useState<{
    result: VDriveStrategySuggestion | null;
    keys: VDriveStrategySectionKey[];
  }>({ result: null, keys: [] });
  const untickedKeys = unticked.result === result ? unticked.keys : [];

  const isMobile = useIsMobile();
  const working = status === 'starting' || status === 'pending';
  const ticked =
    status === 'done' && result
      ? result.sections.filter(
          (section) =>
            isApplicable(section) && !untickedKeys.includes(section.key)
        )
      : [];

  const setTicked = (key: VDriveStrategySectionKey, checked: boolean) =>
    setUnticked({
      result,
      keys: checked
        ? untickedKeys.filter((k) => k !== key)
        : [...untickedKeys, key],
    });

  const footer =
    status === 'idle' ? (
      <Button
        $selected
        $blueGradient
        $w="100%"
        $wSM="100%"
        onClick={() => onStart(note)}
      >
        Start analysis
      </Button>
    ) : status === 'done' && result ? (
      <Div $flex $gap="16px" $gapSM="12px" $columnSM>
        {/* A new run is paid AI work again, so it is a button of its own,
            not a link; the result stays until the new one is started. */}
        <Button $blueGradient $selected $w="100%" $wSM="100%" onClick={onReset}>
          <Div $flex $center $gap="8px" style={{ whiteSpace: 'nowrap' }}>
            <Icon>
              <AIStarsIcon />
            </Icon>
            Start a new analysis
          </Div>
        </Button>
        <Button
          $selected
          $w="100%"
          $wSM="100%"
          disabled={ticked.length === 0}
          onClick={() => onApply(ticked)}
        >
          {ticked.length === 0
            ? 'Nothing selected'
            : `${appliesOnStrategyScreen ? 'Open strategy with' : 'Apply'} ${
                ticked.length
              } ${ticked.length === 1 ? 'change' : 'changes'}`}
        </Button>
      </Div>
    ) : undefined;

  return (
    <ModalPortal
      isOpen={isOpen}
      closeModal={closeModal}
      logo={false}
      textAlign="left"
      width="720px"
      zIndex={zIndex}
      glow={working}
      // The result's two footer buttons stack on a phone, which needs the
      // modal's two-button bottom padding there.
      $numOfFloatingButtons={status === 'done' && isMobile ? 2 : 1}
      footer={footer}
    >
      <Div $flex $alignCenter $gap="8px">
        <Icon>
          <AIStarsIcon />
        </Icon>
        <H5 $m="0">Improve with AI</H5>
      </Div>
      {result && status === 'done' && (
        <Disclaimer $m="4px 0 0" $color={lightTheme.lightGray}>
          {result.name} · {dataLine(result)}
        </Disclaimer>
      )}

      <Div $mt="16">
        {status === 'idle' && (
          <Div $flex $column $gap="16px">
            <P $m="0">
              The AI looks at {name ? `${name}'s` : `this ${scopeLabel}'s`}{' '}
              deliveries from the last 90 days (lateness against the promised
              time, delivery times, fleets, quotes, reassigns and kitchen time)
              and suggests changes section by section. You pick which to apply;
              nothing is saved until you confirm.
            </P>
            <AdminInput
              type="textarea"
              label="Note for AI (optional)"
              placeholder="E.g. the owner says ~20% of weekend deliveries run late"
              $textareaHeight={100}
              $resizable
              maxLength={NOTE_MAX_LENGTH}
              value={note}
              onChange={(e) =>
                setNote(e.target.value.slice(0, NOTE_MAX_LENGTH))
              }
            />
          </Div>
        )}

        {working && <Working />}

        {status === 'failed' && (
          <>
            <P $m="0">{errorMessage}</P>
            <Button
              $blueGradient
              $selected
              $w="100%"
              $wSM="100%"
              $mt="16"
              onClick={() => onStart(note)}
            >
              Try again
            </Button>
          </>
        )}

        {status === 'done' && result && (
          <Div $flex $column $gap="16px">
            <SuggestionOverview result={result} />

            {result.warnings.length > 0 && (
              <Callout tone="warning">
                <BulletList>
                  {result.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </BulletList>
              </Callout>
            )}

            <Div $flex $column $gap="8px">
              {result.sections.map((section) => (
                <SuggestionSection
                  key={section.key}
                  section={section}
                  checked={!untickedKeys.includes(section.key)}
                  onCheckedChange={(checked) => setTicked(section.key, checked)}
                />
              ))}
            </Div>

            {result.risks.length > 0 && (
              <div>
                <P $m="0 0 8px" $bold>
                  Watch out for
                </P>
                <BulletList>
                  {result.risks.map((risk) => (
                    <li key={risk}>{risk}</li>
                  ))}
                </BulletList>
              </div>
            )}

            <Disclaimer $m="0" $color={lightTheme.lightGray}>
              The estimates are the AI&apos;s, worked out from past deliveries,
              not a guarantee.{' '}
              {appliesOnStrategyScreen
                ? 'Applying opens the location strategy with these changes filled in; nothing is saved until you confirm there and save the vDrive settings.'
                : 'Applying only fills the form; nothing is saved until you confirm and save the vDrive settings.'}
            </Disclaimer>
          </Div>
        )}
      </Div>
    </ModalPortal>
  );
};
